import LoaderOverlay from "@edaz/edazkit/LoaderOverlay/index";
import React from "react";
import classnames from "classnames";
import { Form, Col } from "react-bootstrap";
import type { ViewComponent } from "@frui.ts/views";
import { registerView, View } from "@frui.ts/views";
import { Observer, observer } from "mobx-react-lite";
import HeaderWithBackButton from "components/headerWithBackButton";
import FakeInput from "@edaz/edazkit/controls/FakeInput";
import { formatAmount, formatDateTime, formatPhoneNumber } from "utils/localizations";
import PairingByOrderDetailViewModel from "viewModels/finance/paymentsPairing/pairingByOrder/pairingByOrderDetailViewModel";
import type ChargeOrderStatus from "entities/chargeOrderStatus";
import type SourceType from "entities/sourceType";
import { emptyValue } from "utils/emptyValue";

const PairingByOrderDetailView: ViewComponent<PairingByOrderDetailViewModel> = observer(({ vm }) => {
  const className = classnames("bg-light full-width mt-n7 pt-4 rounded-top", { "mb-n4 rounded-bottom": !vm.canBeOrderPaired });

  return (
    <>
      <div className={className}>
        <LoaderOverlay loading={vm.busyWatcher.isBusy}>
          <HeaderWithBackButton
            title={vm.translate("matchingByOrdersTitle")}
            back={{ link: vm.backLink, action: vm.close }}
            className="mb-2"
          />
          {!vm.isInitialized || !vm.item ? (
            <div style={{ minHeight: "100px" }} />
          ) : (
            <div className="row">
              {vm.item.charge && (
                <>
                  <Form.Group as={Col} md={6} lg={4} xl={3}>
                    <Form.Label>{vm.translateAttribute("charge", "vehicle")}</Form.Label>
                    <FakeInput target={vm.item.charge.vehicle} property="licensePlate" />
                  </Form.Group>
                  <Form.Group as={Col} md={6} lg={4} xl={3}>
                    <Form.Label>{vm.translateAttribute("charge", "chargeTypeId")}</Form.Label>
                    <FakeInput
                      target={vm.item.charge}
                      property="priceListItemId"
                      valueFormatter={(priceListItemId?: string) => vm.translateChargeType(priceListItemId)}
                    />
                  </Form.Group>
                  <Form.Group as={Col} sm={6} md={6} lg={4} xl={3}>
                    <Form.Label>{vm.translateAttribute("charge", "validSince")}</Form.Label>
                    <FakeInput
                      target={vm.item.charge}
                      property="validSince"
                      valueFormatter={(date: Date) => formatDateTime(date)}
                    />
                  </Form.Group>
                  <Form.Group as={Col} sm={6} md={6} lg={4} xl={3}>
                    <Form.Label>{vm.translateAttribute("charge", "validUntil")}</Form.Label>
                    <FakeInput
                      target={vm.item.charge}
                      property="validUntil"
                      valueFormatter={(date: Date) => formatDateTime(date)}
                    />
                  </Form.Group>
                </>
              )}
              <Form.Group as={Col} md={6} lg={4} xl={3}>
                <Form.Label>{vm.translateAttribute("chargeOrderSearch", "amount")}</Form.Label>
                <FakeInput target={vm.item} property="amount" valueFormatter={(amount: number) => formatAmount(amount)} />
              </Form.Group>
              <Form.Group as={Col} sm={6} md={6} lg={4} xl={3}>
                <Form.Label>{vm.translateAttribute("chargeOrderSearch", "status")}</Form.Label>
                <FakeInput
                  target={vm.item}
                  property="status"
                  valueFormatter={(status: ChargeOrderStatus) => vm.statusName(status)}
                />
              </Form.Group>
              <Form.Group as={Col} sm={6} md={6} lg={4} xl={3}>
                <Form.Label>{vm.translateAttribute("chargeOrderSearch", "createdAt")}</Form.Label>
                <FakeInput target={vm.item} property="createdAt" valueFormatter={(date: Date) => formatDateTime(date)} />
              </Form.Group>
              <Form.Group as={Col} sm={6} md={6} lg={4} xl={3}>
                <Form.Label>{vm.translateAttribute("chargeOrderSearch", "orderIdent")}</Form.Label>
                <FakeInput target={vm.item} property="orderIdent" />
              </Form.Group>
              <Form.Group as={Col} sm={6} md={6} lg={4} xl={3}>
                <Form.Label>{vm.translateAttribute("chargeOrderSearch", "sourceType")}</Form.Label>
                <FakeInput
                  target={vm.item}
                  property="sourceType"
                  valueFormatter={(sourceType: SourceType) => vm.sourceTypeName(sourceType)}
                />
              </Form.Group>

              {vm.item.charge && (
                <>
                  <Form.Group as={Col} md={6} lg={4} xl={3}>
                    <Form.Label>{vm.translateAttribute("charge", "emailAddress")}</Form.Label>
                    <FakeInput target={vm.item.charge} property="email" />
                  </Form.Group>
                  <Form.Group as={Col} md={6} lg={4} xl={3}>
                    <Form.Label>{vm.translateAttribute("charge", "phoneNumber")}</Form.Label>
                    <FakeInput
                      target={vm.item.charge}
                      property="phoneNumber"
                      valueFormatter={(phoneNumber?: string) => (phoneNumber ? formatPhoneNumber(phoneNumber) : emptyValue)}
                    />
                  </Form.Group>
                </>
              )}
            </div>
          )}
        </LoaderOverlay>
      </div>
      {vm.canBeOrderPaired && <Observer>{() => <View vm={vm.activeChild} />}</Observer>}
    </>
  );
});

registerView(PairingByOrderDetailView, PairingByOrderDetailViewModel);
